/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import styled from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import Calendar from '@design-system/calendar';
import Chip from '@design-system/chip';
import { ETheme } from '@datatype/type';
import * as color from '@constant/color';
import * as size from '@constant/size';

import { useDailyStudyRecordsQuery } from '@hook/iyagigo/useApi';
import { useTypedSelector } from '@hook/common/useStore';
import { EApiLanguageCode, EApiStudyType } from '@datatype/iyagigo/api-type';

const STUDY_SERVICES = [
  { type: EApiStudyType.WORKSHEET, name: '학습지', unit: '학습지', accent: color.BROWN_2 },
  { type: EApiStudyType.VOCABULARY_BOOK, name: '단어집', unit: '단어', accent: color.GREEN_3 },
  { type: EApiStudyType.CLASSROOM_GRAMMAR, name: '기초문법', unit: '강의', accent: color.BROWN_2 },
  { type: EApiStudyType.VOCABULARY_CLOZE, name: '빈칸문제 · 어휘', unit: '문제', accent: color.BLUE_2 },
  { type: EApiStudyType.GRAMMAR_CLOZE, name: '빈칸문제 · 문법', unit: '문제', accent: color.BLUE_2 },
  { type: EApiStudyType.SPEAKING, name: 'AI 말하기', unit: '세션', accent: color.CYAN_3 },
  { type: EApiStudyType.CLASSROOM_CONVERSATION, name: '생활회화', unit: '강의', accent: color.RED_3 },
  { type: EApiStudyType.CHARACTER, name: '글자', unit: '글자', accent: color.GREEN_3 },
  { type: EApiStudyType.CLASSROOM_EXPRESSION, name: '기본표현', unit: '강의', accent: color.BROWN_2 },
];
const dateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const formatStudyTime = (milliseconds: number) => {
  const seconds = Math.floor(milliseconds / 1000);
  if (seconds < 60) return `${seconds}초`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}분${seconds % 60 ? ` ${seconds % 60}초` : ''}`;
  return `${Math.floor(minutes / 60)}시간${minutes % 60 ? ` ${minutes % 60}분` : ''}`;
};

// ############### design ###############
const createDesign = () =>
  ({
    text: { text: { size: 'small', color: color.BLACK_5 }, box: { alignX: 'left', width: '100%' } },
    title: { text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } },
    heading: { text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } },
    value: {
      text: { size: 'large', color: color.BROWN_1, bold: true, oneline: true },
      box: { alignX: 'left', width: '100%' },
    },
  }) as const;
const DESIGN = createDesign();

// 레이아웃은 styled 컨테이너, 텍스트와 컨트롤은 공통 컴포넌트를 사용한다.
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${size.PAGE_PADDING};
  padding-bottom: ${size.MARGIN_4};
  gap: ${size.MARGIN_2};
`;
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${size.MARGIN_1};
`;
const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: 100%;
  gap: ${size.MARGIN_1};
`;
const Card = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: ${size.MARGIN_2};
  padding: ${size.CONTENT_PADDING};
  background: ${color.WHITE_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.BROWN_3};
  border-radius: ${size.BORDER_RADIUS_4};
`;
const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: ${size.MARGIN_1};
  padding: ${size.CONTENT_PADDING};
  background: ${color.BROWN_4};
  border-radius: ${size.BORDER_RADIUS_3};
`;
const Entry = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: ${size.MARGIN_1};
  padding: ${size.MARGIN_1} 0;
  border-bottom: ${size.BORDER_WIDTH_1} solid ${color.GRAY_4};
  &:last-child {
    border-bottom: none;
  }
`;
const EntryText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 5px;
`;
const EntriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const Marker = styled.span<{ $accent: string }>`
  width: 4px;
  height: 30px;
  flex-shrink: 0;
  border-radius: ${size.BORDER_RADIUS_1};
  background: ${(p) => p.$accent};
`;
const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;
const Statistic = () => {
  // ############### state ###############
  const [today] = useState(() => new Date());
  const [range, setRange] = useState<'이번 주' | '이번 달'>('이번 주');
  const [month, setMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(() => new Date(today.getFullYear(), today.getMonth(), today.getDate()));

  // ############### data ###############
  const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - ((today.getDay() + 6) % 7));
  const periodStart = range === '이번 주' ? monday : new Date(today.getFullYear(), today.getMonth(), 1);
  const languageCode =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? EApiLanguageCode.JAPANESE;
  const periodQuery = useDailyStudyRecordsQuery(languageCode, dateKey(periodStart), dateKey(today));
  const monthQuery = useDailyStudyRecordsQuery(
    languageCode,
    dateKey(month),
    dateKey(new Date(month.getFullYear(), month.getMonth() + 1, 0)),
  );
  // 사용하지 않는 서비스는 달력·학습량·시간 합계에서 제외한다.
  const visibleRecords = (monthQuery.data?.data ?? []).filter((record) =>
    STUDY_SERVICES.some((service) => service.type === record.studyType),
  );
  const periodRows = (periodQuery.data?.data ?? []).filter(
    (record) =>
      STUDY_SERVICES.some((service) => service.type === record.studyType) &&
      record.studyDate >= dateKey(periodStart) &&
      record.studyDate <= dateKey(today) &&
      (record.studyCount > 0 || record.studyTime > 0),
  );
  const studyDays = new Set(periodRows.map((record) => record.studyDate)).size;
  const studyTime = periodRows.reduce((sum, record) => sum + record.studyTime, 0);
  const selectedEntries = visibleRecords.filter(
    (record) => record.studyDate === dateKey(selected) && (record.studyCount > 0 || record.studyTime > 0),
  );
  const selectedTime = selectedEntries.reduce((sum, record) => sum + record.studyTime, 0);
  // ############### handler ###############
  const changeMonth = (next: Date) => {
    setMonth(next);
    setSelected(
      dateKey(next) === dateKey(new Date(today.getFullYear(), today.getMonth(), 1))
        ? new Date(today.getFullYear(), today.getMonth(), today.getDate())
        : next,
    );
  };

  // ############### component ###############
  const RangeTabs = (['이번 주', '이번 달'] as const).map((item) => (
    <Chip
      key={item}
      text={item}
      size='small'
      theme={ETheme.BROWN}
      isSelected={range === item}
      handleClick={() => setRange(item)}
    />
  ));
  const studyMarks = Array.from(
    new Set(
      visibleRecords
        .filter((record) => record.studyCount > 0 || record.studyTime > 0)
        .map((record) => record.studyDate),
    ),
    (date) => {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day);
    },
  );
  const Entries = selectedEntries.map((record) => {
    const service = STUDY_SERVICES.find((item) => item.type === record.studyType);
    if (!service) return null;
    return (
      <Entry key={record.studyType}>
        <Marker $accent={service.accent} />
        <EntryText>
          <TextBlock text={service.name} design={DESIGN.title} />
          <TextBlock
            text={record.studyCount > 0 ? `${service.unit} ${record.studyCount}개` : '학습시간 기록'}
            design={DESIGN.text}
          />
        </EntryText>
        <TextBlock text={formatStudyTime(record.studyTime)} design={DESIGN.title} />
      </Entry>
    );
  });
  const ServiceRows = STUDY_SERVICES.map((service) => {
    const entries = periodRows.filter((record) => record.studyType === service.type);
    if (!entries.length) return null;
    const count = entries.reduce((sum, record) => sum + record.studyCount, 0);
    const time = entries.reduce((sum, record) => sum + record.studyTime, 0);
    return (
      <Entry key={service.type}>
        <Marker $accent={service.accent} />
        <EntryText>
          <TextBlock text={service.name} design={DESIGN.title} />
          <TextBlock text={count > 0 ? `${service.unit} ${count}개` : '학습시간 기록'} design={DESIGN.text} />
        </EntryText>
        <TextBlock text={formatStudyTime(time)} design={DESIGN.title} />
      </Entry>
    );
  });
  return (
    <Wrapper>
      <TextBlock text='학습기록' design={DESIGN.heading} />
      <Row>
        <TextBlock text='차곡차곡 쌓인 나의 학습' design={DESIGN.title} />
        <Tabs>{RangeTabs}</Tabs>
      </Row>
      <Summary>
        <SummaryCard>
          <TextBlock text={`${range} 학습한 날`} design={DESIGN.text} />
          <TextBlock
            text={periodQuery.isPending || periodQuery.isError ? '—' : `${studyDays}일`}
            design={DESIGN.value}
          />
        </SummaryCard>
        <SummaryCard>
          <TextBlock text='총 학습시간' design={DESIGN.text} />
          <TextBlock
            text={
              periodQuery.isPending || periodQuery.isError
                ? '—'
                : studyTime < 60_000
                  ? formatStudyTime(studyTime)
                  : `${Math.floor(studyTime / 60_000)}분`
            }
            design={DESIGN.value}
          />
        </SummaryCard>
      </Summary>
      {(periodQuery.isError || monthQuery.isError) && (
        <TextBlock text='학습기록을 불러오지 못했어요.' design={DESIGN.text} />
      )}
      <TextBlock text='복습한 콘텐츠도 학습할 때마다 포함해요.' design={DESIGN.text} />
      <TextBlock text='학습 달력' design={DESIGN.title} />
      <Calendar
        marks={studyMarks}
        calendarType='study'
        month={month}
        handleMonthNavigationClick={changeMonth}
        selectedDate={selected}
        handleDateClick={setSelected}
        theme={ETheme.BROWN}
        size='large'
        width='100%'
      />
      <TextBlock text='점이 있는 날은 학습한 날이에요. 날짜를 눌러 기록을 확인해보세요.' design={DESIGN.text} />
      <Card>
        <Row>
          <TextBlock
            text={`${selected.getMonth() + 1}월 ${selected.getDate()}일${dateKey(selected) === dateKey(today) ? ' · 오늘' : ''}`}
            design={DESIGN.title}
          />
          <TextBlock
            text={selectedEntries.length ? `총 ${formatStudyTime(selectedTime)}` : ''}
            design={{ ...DESIGN.text, box: { alignX: 'left' } }}
          />
        </Row>
        {selectedEntries.length ? (
          <EntriesContainer>{Entries}</EntriesContainer>
        ) : (
          <TextBlock
            text={
              monthQuery.isPending
                ? '학습기록을 불러오고 있어요.'
                : monthQuery.isError
                  ? '날짜별 기록을 불러오지 못했어요.'
                  : '이날은 학습기록이 없어요.'
            }
            design={{ ...DESIGN.text, box: { alignX: 'left', padding: '16px 0' } }}
          />
        )}
      </Card>
      <Card>
        <TextBlock text={`${range}, 이런 학습을 했어요`} design={DESIGN.title} />
        {periodRows.length ? (
          ServiceRows
        ) : (
          <TextBlock
            text={
              periodQuery.isPending
                ? '학습기록을 불러오고 있어요.'
                : periodQuery.isError
                  ? '서비스별 기록을 불러오지 못했어요.'
                  : '학습을 시작하면 서비스별 기록이 여기에 쌓여요.'
            }
            design={DESIGN.text}
          />
        )}
      </Card>
    </Wrapper>
  );
};
export default Statistic;
